import { QuestionnaireQuestion, QuestionnaireAnswers } from "../types/questionnaire";
import { getVisibleQuestions } from "./questionnaire-engine";
import { EMAIL_CAPTURE_AFTER_QUESTION_COUNT, type FlowStep } from "./flow-steps";

export type AccountSubStep = 'email' | 'login';

/** Canonical home for AccountRegister's 2-screen step model; re-exported from AccountRegister.tsx. */
export type AccountDetailsStep = 'identity' | 'access';

/** Major journey sections for "Step X of Y · section name" */
export type ProgressSectionId =
  | 'aboutYou'
  | 'healthQuestions'
  | 'yourTreatment'
  | 'accountSetup'
  | 'checkoutDetails';

export const PROGRESS_SECTIONS: readonly ProgressSectionId[] = [
  'aboutYou',
  'healthQuestions',
  'yourTreatment',
  'accountSetup',
  'checkoutDetails',
] as const;

interface ProgressCalculation {
  currentStep: number;
  totalSteps: number;
  percentage: number;
  /** 1-based section index for "Step X of Y" */
  sectionIndex: number;
  totalSections: number;
  sectionId: ProgressSectionId;
}

/**
 * Map a flow step to its major section.
 * Welcome has no progress UI; treat as aboutYou if ever queried.
 *
 * @param isEmailCaptureMidQuestionnaire'
 */
export function getProgressSectionId(
  flowStep: FlowStep,
  isEmailCaptureMidQuestionnaire: boolean = false
): ProgressSectionId {
  switch (flowStep) {
    case 'welcome':
    case 'intro':
    case 'state-check':
      return 'aboutYou';
    case 'login':
    case 'email-capture':
      return isEmailCaptureMidQuestionnaire ? 'healthQuestions' : 'aboutYou';
    case 'health-questions-intro':
    case 'questionnaire':
    case 'questionnaire-summary':
      return 'healthQuestions';
    case 'dose-selection':
    case 'quantity-selection':
    case 'subscription-selection':
      return 'yourTreatment';
    case 'account-details':
      return 'accountSetup';
    case 'shipping-info':
    case 'file-upload':
    case 'cart-summary':
    case 'checkout':
    case 'complete':
      return 'checkoutDetails';
    default:
      return 'aboutYou';
  }
}

/**
 * Calculate unified progress across the entire journey
 *
 * @param flowStep - Current main flow step
 * @param accountSubStep - Current sub-step within the email-capture step (if applicable)
 * @param accountDetailsStep - Current sub-step within account-details (if applicable)
 * @param questions - All questionnaire questions
 * @param answers - Current questionnaire answers
 * @param currentQuestionIndex - Index of current question in visible questions (if in questionnaire)
 * @param willTraverseAccountDetails - False for customers who already have both a Shopify and
 *   an MDI account
 * @param isEmailCaptureMidQuestionnaire
 * @returns Progress calculation with micro-steps, percentage, and major section info
 */
export function calculateUnifiedProgress(
  flowStep: FlowStep,
  accountSubStep: AccountSubStep | null,
  accountDetailsStep: AccountDetailsStep | null,
  questions: QuestionnaireQuestion[],
  answers: QuestionnaireAnswers,
  currentQuestionIndex: number = 0,
  willTraverseAccountDetails: boolean = true,
  isEmailCaptureMidQuestionnaire: boolean = false
): ProgressCalculation {
  // Calculate dynamic questionnaire steps based on current answers
  const visibleQuestions = questions.length > 0
    ? getVisibleQuestions(questions, answers)
    : [];

  const questionnaireSteps = visibleQuestions.length;

  // Define step structure:
  // 1. Welcome (no progress bar - step 0)
  // 2. Intro
  // 3. State check
  // 4+. Questionnaire: each visible question is a step, with a mid-questionnaire interruption
  //     for email capture (email (1), login (2)) after EMAIL_CAPTURE_AFTER_QUESTION_COUNT
  //     questions, then a "health questions starting" transition, then the rest of the questions
  // Then: a questionnaire-summary screen
  // Then: dose-selection, quantity-selection, subscription-selection
  // Then (conditionally): account-details identity (1), access (2) — access now also collects
  //     password + creates the Shopify/MDI account for a brand-new registrant (SRX-198)
  // Then: shipping-info, file-upload

  let currentStep = 0;
  let totalSteps = 0;

  const introSteps = 1;
  const stateCheckSteps = 1;
  const emailCaptureSteps = 2;
  const healthQuestionsIntroSteps = 1;
  const questionnaireSummarySteps = 1;
  const productSelectionSteps = 3; // dose, quantity, subscription — shipping/file-upload have their own bucket now
  const accountDetailsSteps = 2; // identity, access
  const postAccountSteps = 2; // shipping-info, file-upload

  const accountDetailsBudget = willTraverseAccountDetails ? accountDetailsSteps : 0;

  // Everything before the questionnaire begins.
  const preQuestionnaireSteps = introSteps + stateCheckSteps;
  const questionnairePhaseSteps =
    questionnaireSteps +
    emailCaptureSteps +
    healthQuestionsIntroSteps +
    questionnaireSummarySteps;

  totalSteps =
    preQuestionnaireSteps +
    questionnairePhaseSteps +
    productSelectionSteps +
    accountDetailsBudget +
    postAccountSteps;

  // Calculate current step based on flow
  switch (flowStep) {
    case 'welcome':
      currentStep = 0; // Welcome doesn't show progress
      break;

    case 'intro':
      currentStep = introSteps;
      break;

    case 'state-check':
      currentStep = preQuestionnaireSteps;
      break;

    case 'email-capture':
    case 'login':
      // We're in the email-capture screen, map sub-step to progress
      if (isEmailCaptureMidQuestionnaire) {
        const base = preQuestionnaireSteps + EMAIL_CAPTURE_AFTER_QUESTION_COUNT;
        currentStep = accountSubStep === 'login' ? base + 2 : base + 1;
      } else {
        currentStep = accountSubStep === 'login' ? stateCheckSteps + 2 : stateCheckSteps + 1;
      }
      break;

    case 'health-questions-intro':
      currentStep =
        preQuestionnaireSteps +
        EMAIL_CAPTURE_AFTER_QUESTION_COUNT +
        emailCaptureSteps +
        healthQuestionsIntroSteps;
      break;

    case 'questionnaire': {
      // Once past the email-capture interrupt point, the transition screens' weight has
      // already been "spent" — add it so the percentage doesn't under-count.
      const passedEmailCaptureInterrupt = (currentQuestionIndex + 1) > EMAIL_CAPTURE_AFTER_QUESTION_COUNT;
      currentStep =
        preQuestionnaireSteps +
        (currentQuestionIndex + 1) +
        (passedEmailCaptureInterrupt
          ? emailCaptureSteps + healthQuestionsIntroSteps
          : 0);
      break;
    }

    case 'questionnaire-summary':
      currentStep = preQuestionnaireSteps + questionnairePhaseSteps;
      break;

    case 'dose-selection':
      currentStep = preQuestionnaireSteps + questionnairePhaseSteps + 1;
      break;

    case 'quantity-selection':
      currentStep = preQuestionnaireSteps + questionnairePhaseSteps + 2;
      break;

    case 'subscription-selection':
      currentStep = preQuestionnaireSteps + questionnairePhaseSteps + 3;
      break;

    case 'account-details':
      currentStep =
        preQuestionnaireSteps +
        questionnairePhaseSteps +
        productSelectionSteps +
        (accountDetailsStep === 'access' ? 2 : 1);
      break;

    case 'shipping-info':
      currentStep =
        preQuestionnaireSteps +
        questionnairePhaseSteps +
        productSelectionSteps +
        accountDetailsBudget +
        1;
      break;

    case 'file-upload':
      currentStep =
        preQuestionnaireSteps +
        questionnairePhaseSteps +
        productSelectionSteps +
        accountDetailsBudget +
        2;
      break;

    case 'cart-summary':
      // After all product selection is complete
      currentStep = totalSteps;
      break;

    case 'checkout':
      // Checkout is at the end, same as complete
      currentStep = totalSteps;
      break;

    case 'complete':
      currentStep = totalSteps;
      break;

    default:
      currentStep = 0;
  }

  const percentage = totalSteps > 0
    ? Math.min((currentStep / totalSteps) * 100, 100)
    : 0;

  const sectionId = getProgressSectionId(flowStep, isEmailCaptureMidQuestionnaire);
  const sectionIndex = PROGRESS_SECTIONS.indexOf(sectionId) + 1;

  return {
    currentStep,
    totalSteps,
    percentage,
    sectionIndex,
    totalSections: PROGRESS_SECTIONS.length,
    sectionId,
  };
}

/**
 * Get current question index in visible questions list
 */
export function getCurrentQuestionStep(
  questions: QuestionnaireQuestion[],
  answers: QuestionnaireAnswers,
  currentQuestionId: string
): number {
  const visibleQuestions = getVisibleQuestions(questions, answers);
  const index = visibleQuestions.findIndex(
    (q) => q.partner_questionnaire_question_id === currentQuestionId
  );
  return index >= 0 ? index : 0;
}
